import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Grid, Paper, Chip, CircularProgress,
  Card, CardContent, List, ListItem, ListItemText, ListItemIcon,
  useMediaQuery,
} from "@mui/material";
import {
  Language, Phone, Campaign, TrendingUp, People, Web,
  LocalFireDepartment, Whatshot, AcUnit, CallMade,
} from "@mui/icons-material";
import { SmbDashboardProvider, useSmbDashboard } from "../../contexts/SmbDashboardContext";
import { getOnboardingStatus } from "../../api/smb.api";

const DashboardContent = () => {
  const { dashboardData, events, wsConnected, isLoading } = useSmbDashboard();
  const isMobile = useMediaQuery("(max-width:768px)");

  if (isLoading || !dashboardData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#3B82F6" }} />
      </Box>
    );
  }

  const { social_accounts, website, calling_agent, leads, content } = dashboardData;

  const statCards = [
    { label: "Social Accounts", value: social_accounts?.connected || 0, icon: <Campaign sx={{ color: "#E4405F" }} />, color: "#E4405F" },
    { label: "Website", value: website?.status === "active" ? "Active" : "Inactive", icon: <Web sx={{ color: "#059669" }} />, color: "#059669" },
    { label: "Total Calls", value: calling_agent?.total_calls || 0, icon: <Phone sx={{ color: "#3B82F6" }} />, color: "#3B82F6" },
    { label: "Today's Calls", value: calling_agent?.today_calls || 0, icon: <CallMade sx={{ color: "#7C3AED" }} />, color: "#7C3AED" },
    { label: "Content Published", value: content?.published || 0, icon: <TrendingUp sx={{ color: "#F59E0B" }} />, color: "#F59E0B" },
    { label: "Total Leads", value: leads?.total || 0, icon: <People sx={{ color: "#10B981" }} />, color: "#10B981" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#101220", color: "#fff", px: isMobile ? 2 : 4, py: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>SMB Dashboard</Typography>
        <Chip
          label={wsConnected ? "Live" : "Offline"}
          size="small"
          sx={{ bgcolor: wsConnected ? "#059669" : "#6B7280", color: "#fff" }}
        />
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 2, p: 2, textAlign: "center" }}>
              {stat.icon}
              <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>{stat.value}</Typography>
              <Typography variant="caption" sx={{ color: "#9CA3AF" }}>{stat.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Lead Classification */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Lead Classification</Typography>
            <Grid container spacing={2}>
              {[
                { label: "Hot Leads", count: leads?.hot || 0, icon: <LocalFireDepartment />, color: "#EF4444" },
                { label: "Warm Leads", count: leads?.warm || 0, icon: <Whatshot />, color: "#F59E0B" },
                { label: "Cold Leads", count: leads?.cold || 0, icon: <AcUnit />, color: "#3B82F6" },
              ].map((item, i) => (
                <Grid item xs={4} key={i}>
                  <Card sx={{ bgcolor: "#141626", border: "1px solid #282D51", textAlign: "center", py: 2, borderRadius: 2 }}>
                    <Box sx={{ color: item.color }}>{item.icon}</Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>{item.count}</Typography>
                    <Typography variant="caption" sx={{ color: "#9CA3AF" }}>{item.label}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 3, maxHeight: 350, overflow: "auto" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Activity</Typography>
            {events.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#9CA3AF", textAlign: "center", py: 4 }}>
                No recent activity
              </Typography>
            ) : (
              <List dense>
                {events.slice(0, 10).map((event, i) => (
                  <ListItem key={i} sx={{ borderBottom: "1px solid #282D51" }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {event.type === "call_completed" && <Phone sx={{ color: "#3B82F6", fontSize: 18 }} />}
                      {event.type === "lead_created" && <People sx={{ color: "#10B981", fontSize: 18 }} />}
                      {event.type === "content_published" && <Campaign sx={{ color: "#F59E0B", fontSize: 18 }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={event.type?.replace(/_/g, " ")}
                      secondary={event.time}
                      primaryTypographyProps={{ variant: "body2", sx: { color: "#E5E7EB" } }}
                      secondaryTypographyProps={{ variant: "caption", sx: { color: "#6B7280" } }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const SmbDashboard = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [resolvedBusinessId, setResolvedBusinessId] = useState(businessId);

  useEffect(() => {
    if (!businessId) {
      getOnboardingStatus().then((result) => {
        if (result?.status && result?.data?.has_business) {
          setResolvedBusinessId(result.data.business_id);
        } else {
          navigate("/smb/onboarding");
        }
      });
    }
  }, [businessId, navigate]);

  if (!resolvedBusinessId) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#3B82F6" }} />
      </Box>
    );
  }

  return (
    <SmbDashboardProvider businessId={resolvedBusinessId}>
      <DashboardContent />
    </SmbDashboardProvider>
  );
};

export default SmbDashboard;
